import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, cn, Flex, Icon, Text } from '@kognitos/lattice';

import type { V1Run } from '@/generated/hooks/jeeves/models';
import {
  getRunStateBadgeVariant,
  getRunStateDisplay,
  mapRunStateToFilterValue,
} from '@/modules/automation-details/utils';
import {
  ERunHeaderControlAction,
  type TRunHeaderControlAction,
} from '@/modules/automation-run/utils/run-utils';
import {
  DATE_FORMAT_TOKENS,
  formatDate,
  formatEpochMs,
  parseTimestamp,
} from '@/shared/utils/datetime-utils';

export interface ITestRunBannerProps {
  /** Undefined while the run's `GetRun` snapshot is still loading — the
   *  banner shows just the "Viewing" text and close button until then,
   *  same as it does for a run with no failure/badge-worthy state. */
  run: V1Run | undefined;
  /** Placeholder shown only while `run` is still loading — once it
   *  resolves, `run.create_time` (jeeves' own immutable run-creation
   *  clock) takes over, since it's already on hand and more authoritative
   *  than this rain-event-log-derived value. */
  startedAtMs?: number;
  onClose: () => void;
  /** A test was just requested and its run isn't known yet. */
  isStarting?: boolean;
  /** Which run control to offer, from `useRunControl`. Null renders no
   *  control — the banner is then just status + close, as before. That covers
   *  a completed or failed run, one awaiting guidance, and a snapshot that
   *  hasn't loaded yet (see `getRunHeaderControlAction`). */
  runControlAction?: TRunHeaderControlAction;
  /** Whether the pause/continue request is in flight. */
  isRunControlPending?: boolean;
  /** Whether a pause is in progress, from click through to the run reporting
   *  it. Keeps the button's spinner up past the point where the request
   *  itself resolves, so the whole pause reads as one action. */
  isRunControlPausing?: boolean;
  /** Whether the control should be non-interactive (in flight, mid-`stopping`,
   *  or missing request context). */
  isRunControlDisabled?: boolean;
  /** Runs the offered control. Omitted hides the control entirely. Resolving
   *  false means it failed, which rolls back the optimistic pausing state
   *  (see `useRunControl.control`). */
  onRunControl?: () => void | Promise<boolean | void>;
}

/**
 * Persistent header for a focused test run — its status and, while the run is
 * still live, the pause/resume control (same `PauseRun`/`ContinueRun` pair the
 * run page header drives, via the shared `useRunControl`). Without it the only
 * way off this view was the close button, which unfocuses the banner and
 * leaves the run executing.
 *
 * Beyond that it is just status (same
 * `getRunStateDisplay`/`getRunStateBadgeVariant` mapping the runs table
 * uses). A failed run's error (`RunState.failed.description`) is shown once,
 * on the failing step itself (`ManifestStepCard`'s error block), which the
 * SOP view auto-opens and scrolls to — not duplicated here. The run's real
 * inputs/outputs are likewise shown as a Value column directly on
 * `ManifestSopView`'s existing Inputs/Outputs schema tables (see
 * `getInputValue`/`getOutputValue` on `AutomationEditor`).
 */
export function TestRunBanner({
  run,
  startedAtMs,
  onClose,
  isStarting = false,
  runControlAction,
  isRunControlPending = false,
  isRunControlPausing = false,
  isRunControlDisabled = false,
  onRunControl,
}: ITestRunBannerProps) {
  const { t: tAutomation } = useTranslation('automation');
  const isPauseControl = runControlAction === ERunHeaderControlAction.PAUSE;
  const isExecuting = Boolean(run?.state?.executing);
  // Tied to the run's own state rather than to `isRunControlPausing`, so the
  // pulse starts when the badge actually reads "Stopping" — pulsing it while
  // it still says "Executing" would be animating the wrong claim.
  const isStopping = Boolean(run?.state?.stopping);

  // `PauseRun` resolves a second or two before `stopping` reaches run state,
  // and in that seam the hook reports nothing in progress — the button would
  // drop back to an idle "Pause run" mid-pause. This just shows the pausing
  // state the incoming update is about to bring, and steps aside as soon as
  // the run stops reporting `executing`. Optimistic and local by design: it
  // survives no reload, so a refresh always shows whatever the run really is.
  const [hasRequestedPause, setHasRequestedPause] = useState(false);

  const isPausing =
    isPauseControl &&
    (isRunControlPausing || (hasRequestedPause && isExecuting));
  // Resume has no equivalent multi-stage window, so its loading state is just
  // the request; pausing spans click → request → unwind as one.
  const isControlLoading = isPauseControl ? isPausing : isRunControlPending;

  const handleRunControl = async () => {
    // Arm on pause, disarm on resume — otherwise a resumed run returning to
    // `executing` would re-read as pausing off the stale flag.
    setHasRequestedPause(isPauseControl);
    // A failed pause has to roll the optimistic flag back, or the run keeps
    // reporting `executing`, nothing ever clears the flag, and the button is
    // stranded on a disabled "Pausing…" next to a failure toast.
    //
    // Two ways it can fail. `useRunControl.control` handles its own errors and
    // reports false; a handler that rejects instead (the prop allows one) would
    // otherwise skip the rollback and escape onClick as an unhandled rejection.
    // Only those two roll back — not any non-`true` result, since a handler
    // returning void is normal and must leave the bridge armed.
    try {
      const succeeded = await onRunControl?.();
      if (succeeded === false) {
        setHasRequestedPause(false);
      }
    } catch {
      setHasRequestedPause(false);
    }
  };
  const viewingTime = run?.create_time
    ? formatDate(
        parseTimestamp(run.create_time),
        DATE_FORMAT_TOKENS.SHORT_DATE_TIME,
      )
    : formatEpochMs(
        startedAtMs ?? Date.now(),
        DATE_FORMAT_TOKENS.SHORT_DATE_TIME,
      );

  // Pending and executing read as one state, so the badge doesn't flip
  // between them while a test gets going.
  const runStateFilter = mapRunStateToFilterValue(run?.state);
  const isTesting =
    isStarting ||
    runStateFilter === 'Pending' ||
    runStateFilter === 'Executing';

  return (
    <Flex
      align="center"
      justify="between"
      className="mb-3 w-full shrink-0 rounded-lg border border-border bg-muted/50 px-4 py-2"
      data-testid="test-run-focus-banner"
    >
      <Flex align="center" gap={2}>
        {isTesting ? (
          <Badge variant="secondary" data-testid="test-run-focus-banner-status">
            {tAutomation('automation.manifestSopView.testRun.testing')}
          </Badge>
        ) : (
          run && (
            <Badge
              variant={getRunStateBadgeVariant(run.state)}
              // The "Stopping" badge pulses for as long as the run takes to
              // unwind, so the state itself reads as in-transition rather than
              // as a settled label.
              className={cn(
                isStopping && 'animate-pulse motion-reduce:animate-none',
              )}
              data-testid="test-run-focus-banner-status"
            >
              {getRunStateDisplay(run.state).text}
            </Badge>
          )
        )}
        <Text level="small">
          {tAutomation('automation.manifestSopView.testRun.viewing', {
            time: viewingTime,
          })}
        </Text>
      </Flex>
      <Flex align="center" gap={1}>
        {runControlAction && onRunControl && (
          <Button
            variant="ghost"
            size="sm"
            data-testid="test-run-focus-banner-run-control"
            onClick={handleRunControl}
            disabled={isRunControlDisabled}
            isLoading={isControlLoading}
          >
            <Icon type={isPauseControl ? 'Pause' : 'Play'} size="sm" />
            {tAutomation(
              isPausing
                ? 'automation.manifestSopView.testRun.pausing'
                : isPauseControl
                  ? 'automation.manifestSopView.testRun.pause'
                  : 'automation.manifestSopView.testRun.resume',
            )}
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={tAutomation('automation.manifestSopView.testRun.close')}
          data-testid="test-run-focus-banner-close"
          onClick={onClose}
        >
          <Icon type="X" size="sm" />
        </Button>
      </Flex>
    </Flex>
  );
}
